import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import ReactTooltip from 'react-tooltip';

import { ActionButton } from 'components/ui/Button';
import { ErrorField } from 'pages/ManageClubEurostar/ManageClubEurostar.styles';
import { TooltipContainer } from 'pages/VoucherDetails/VoucherDetails.styles';
import Tabs from 'components/ui/Tabs';
import { ACCESSIBLE_TRAVEL_TYPE, ANIMALS_ASSISTANCE_TYPE, GENERAL_TYPE, BLOCKED_SEATS_TYPE } from 'constants/addPassengers';

import {
  PassengerTypeActionContainer,
  PassengerTypeContainer,
  PassengerTypeLabel,
  StyledAnimalsAlreadyBooked,
  StyledAnimalsAlreadyBookedContent
} from './AddPassengersCta.styles';
import { getGroupPassengerTypeOptions } from './AddPassengersCta.utils';
import GeneralBookings from './GeneralBookings';
import AnimalsBookings from './AnimalsBookings';
import BlockedSeats from './BlockedSeats';
import WheelchairPassengers from './WheelchairPassengers';
import NonWheelchairPassengers from './NonWheelchairPassengers';

const WHEELCHAIR = 'Wheelchairs';
const NON_WHEELCHAIR = 'Non-wheelchair';

const GroupBookings = ({
  register,
  getValues,
  passengersType,
  setPassengerType,
  errors,
  trigger,
  numberOfWheelchairUsers,
  numberOfWheelchairCompanions,
  numberOfNonWheelchairCompanions,
  actionComponent,
  bookingHasAnimals,
  isContinentalBooking,
  isTravelAgent,
  setValue,
  hasSnapTickets,
  hasWheelchairPassenger,
  hasWheelchairCompanion,
  hasNonWheelchairCompanion,
  isLoyaltyBooking,
  isDutyTollFree
}) => {
  const [selectedTab, setSelectedTab] = useState(WHEELCHAIR);
  const handleGeneralClick = () => setPassengerType(GENERAL_TYPE);
  const handleAssistanceClick = () => setPassengerType(ACCESSIBLE_TRAVEL_TYPE);
  const handleAnimalsAssistanceClick = () => setPassengerType(ANIMALS_ASSISTANCE_TYPE);
  const handleBlockedSeatsClick = () => setPassengerType(BLOCKED_SEATS_TYPE);
  const {
    canShowGeneral,
    canShowSpecialAssistance,
    canShowAnimals,
    isAnimalsDisabled,
    shouldShowAnimalsAlreadyBooked,
    canShowBlockedSeats
  } = getGroupPassengerTypeOptions({
    bookingHasAnimals,
    isContinentalBooking,
    isTravelAgent,
    hasSnapTickets,
    hasWheelchairPassenger,
    hasWheelchairCompanion,
    hasNonWheelchairCompanion,
    isLoyaltyBooking,
    isDutyTollFree
  });

  if (passengersType === GENERAL_TYPE) {
    return (
      <>
        <GeneralBookings register={register} errors={errors} />
        {actionComponent}
      </>
    );
  }

  if (passengersType === ANIMALS_ASSISTANCE_TYPE) {
    return (
      <>
        <AnimalsBookings register={register} errors={errors} />
        {errors.guideDogs ? (
          <ErrorField
            data-testid="assistance-animal-passengers-error"
            data-cy="assistance-animal-passengers-error"
            id="assistance-animal-passengers-error"
          >
            <FormattedMessage id="addPax.onlyOneAssistanceAnimal" />
          </ErrorField>
        ) : null}
        {actionComponent}
      </>
    );
  }

  if (passengersType === ACCESSIBLE_TRAVEL_TYPE) {
    return (
      <>
        <Tabs
          selectedValue={selectedTab}
          onClick={setSelectedTab}
          tabs={[
            {
              value: WHEELCHAIR,
              label: <FormattedMessage id="addPassengers.addWheelchairPassenger" />
            },
            {
              value: NON_WHEELCHAIR,
              label: <FormattedMessage id="addPassengers.addNonWheelchairPassenger" />
            }
          ]}
        />

        {selectedTab === WHEELCHAIR && (
          <WheelchairPassengers
            register={register}
            getValues={getValues}
            trigger={trigger}
            errors={errors}
            setValue={setValue}
            numberOfWheelchairUsers={numberOfWheelchairUsers}
            numberOfWheelchairCompanions={numberOfWheelchairCompanions}
            numberOfNonWheelchairCompanions={numberOfNonWheelchairCompanions}
            actionComponent={actionComponent}
          />
        )}

        {selectedTab === NON_WHEELCHAIR && (
          <NonWheelchairPassengers
            register={register}
            setValue={setValue}
            getValues={getValues}
            trigger={trigger}
            errors={errors}
            numberOfWheelchairUsers={numberOfWheelchairUsers}
            numberOfWheelchairCompanions={numberOfWheelchairCompanions}
            numberOfNonWheelchairCompanions={numberOfNonWheelchairCompanions}
            actionComponent={actionComponent}
          />
        )}
      </>
    );
  }

  if (passengersType === BLOCKED_SEATS_TYPE) {
    return (
      <>
        <BlockedSeats register={register} errors={errors} />
        {actionComponent}
      </>
    );
  }

  return (
    <PassengerTypeContainer>
      <PassengerTypeLabel>
        <FormattedMessage id="addPassengers.passengerTypeLabel" />
      </PassengerTypeLabel>
      <PassengerTypeActionContainer>
        {canShowGeneral && (
          <ActionButton data-testid="general-select" data-cy="general-select" onClick={handleGeneralClick}>
            <FormattedMessage id="addPassengers.general" />
          </ActionButton>
        )}
        {canShowSpecialAssistance && (
          <ActionButton data-testid="special-assistance-select" data-cy="special-assistance-select" onClick={handleAssistanceClick}>
            <FormattedMessage id="addPassengers.specialAssistance" />
          </ActionButton>
        )}
        {shouldShowAnimalsAlreadyBooked ? (
          <>
            <StyledAnimalsAlreadyBooked
              data-testid="animals-assistance-select"
              data-cy="animals-assistance-select"
              data-for="animals-tooltip"
              data-tip
            >
              <StyledAnimalsAlreadyBookedContent>
                <FormattedMessage id="addAssistance.assistanceAnimal" />
              </StyledAnimalsAlreadyBookedContent>
            </StyledAnimalsAlreadyBooked>
            <ReactTooltip className="tooltip" id="animals-tooltip" data-cy="animals-tooltip" place="bottom">
              <TooltipContainer>
                <FormattedMessage id="addPax.assistanceAnimalAlreadyBooked" />
              </TooltipContainer>
            </ReactTooltip>
          </>
        ) : (
          canShowAnimals && (
            <ActionButton
              data-testid="animals-assistance-select"
              data-cy="animals-assistance-select"
              onClick={handleAnimalsAssistanceClick}
              disabled={isAnimalsDisabled}
            >
              <FormattedMessage id="addAssistance.assistanceAnimal" />
            </ActionButton>
          )
        )}
        {canShowBlockedSeats && (
          <ActionButton data-testid="blocked-seats-select" data-cy="blocked-seats-select" onClick={handleBlockedSeatsClick}>
            <FormattedMessage id="addPassengers.blockedSeats" />
          </ActionButton>
        )}
      </PassengerTypeActionContainer>
    </PassengerTypeContainer>
  );
};

GroupBookings.propTypes = {
  register: PropTypes.func,
  getValues: PropTypes.func,
  passengersType: PropTypes.oneOf([GENERAL_TYPE, ACCESSIBLE_TRAVEL_TYPE, BLOCKED_SEATS_TYPE, null]),
  setPassengerType: PropTypes.func,
  errors: PropTypes.shape({
    wheelchairAdults: PropTypes.object,
    wheelchairChild: PropTypes.object,
    wheelchairCompanions: PropTypes.object,
    nonWheelchairCompanions: PropTypes.object,
    guideDogs: PropTypes.object
  }),
  trigger: PropTypes.func,
  hasSnapTickets: PropTypes.bool,
  hasNonWheelchairCompanion: PropTypes.bool,
  hasWheelchairPassenger: PropTypes.bool,
  hasWheelchairCompanion: PropTypes.bool,
  numberOfWheelchairUsers: PropTypes.number,
  numberOfWheelchairCompanions: PropTypes.number,
  numberOfNonWheelchairCompanions: PropTypes.number,
  actionComponent: PropTypes.node,
  bookingHasAnimals: PropTypes.bool,
  isContinentalBooking: PropTypes.bool,
  isLoyaltyBooking: PropTypes.bool,
  isDutyTollFree: PropTypes.bool,
  isTravelAgent: PropTypes.bool,
  setValue: PropTypes.func
};

export default GroupBookings;
